/**
 * Meta Pixel + Conversions API client helper.
 *
 * Every conversion fires TWICE in lockstep:
 *   1. Browser-side via `window.fbq('track', name, data, {eventID})`
 *   2. Server-side via POST /api/meta/track (CAPI)
 *
 * Both calls use the SAME event_id so Meta dedupes within 48h. Net
 * effect: we keep client-side speed/match-keys (fbp/fbc cookies, IP, UA)
 * AND get server-side resilience against ad-blockers / iOS 14.5 ITP.
 *
 * Usage:
 *   import { trackEvent } from "../lib/metaPixel";
 *   trackEvent("Lead", { content_name: "Signup" });
 *
 *   // Page view on every SPA route change is auto-fired by usePixelRouteTracker
 *   // mounted at the App.js layer.
 */
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

/** Read a cookie value by name. Returns "" if not set. */
function readCookie(name) {
  if (typeof document === "undefined") return "";
  const m = document.cookie.match(new RegExp("(^| )" + name + "=([^;]+)"));
  return m ? decodeURIComponent(m[2]) : "";
}

/** Generate a unique event_id used to dedupe browser + server events.
 *  Format: <timestamp>_<random> — stays short, sortable, and globally unique. */
function newEventId() {
  return Date.now().toString(36) + "_" + Math.random().toString(36).slice(2, 11);
}

/**
 * Fire a Meta standard event in both the browser pixel AND CAPI.
 *
 * @param {string} eventName  e.g. "Lead", "CompleteRegistration", "StartTrial",
 *                             "Subscribe", "Purchase", "InitiateCheckout"
 * @param {object} customData Optional event params (value, currency, etc.)
 * @param {object} opts       { deterministicId?: string } — pass when you
 *                             want client/server dedupe with a backend
 *                             event that already fired (e.g. signup).
 */
export function trackEvent(eventName, customData = {}, opts = {}) {
  const eventId = opts.deterministicId || newEventId();

  // 1. Browser pixel — best match keys (fbp/fbc cookies, client UA/IP)
  try {
    if (typeof window !== "undefined" && typeof window.fbq === "function") {
      window.fbq("track", eventName, customData, { eventID: eventId });
    }
  } catch (e) {
    // Pixel JS hasn't loaded yet (likely an ad-blocker). CAPI still
    // fires below — that's the whole point of the dual-track design.
  }

  // 2. Server-side CAPI — bypasses every client-side blocker.
  // Fire-and-forget; never await, never block UX, never surface errors.
  try {
    const fbp = readCookie("_fbp");
    const fbc = readCookie("_fbc");
    const token = (typeof localStorage !== "undefined" && localStorage.getItem("id_token")) || null;

    const headers = { "Content-Type": "application/json" };
    if (token) headers["Authorization"] = "Bearer " + token;

    axios.post(
      `${API}/api/meta/track`,
      {
        event_name: eventName,
        event_id: eventId,
        source_url: typeof window !== "undefined" ? window.location.href : "",
        fbp: fbp || undefined,
        fbc: fbc || undefined,
        custom_data: customData,
      },
      { headers, adapter: "xhr", timeout: 5000 },
    ).catch(() => {});
  } catch (e) {
    // Swallow — tracking should never break the app.
  }

  return eventId;
}

/** Track a custom (non-standard) event. Same dual-fire as trackEvent. */
export function trackCustomEvent(eventName, customData = {}) {
  const eventId = newEventId();
  try {
    if (typeof window !== "undefined" && typeof window.fbq === "function") {
      window.fbq("trackCustom", eventName, customData, { eventID: eventId });
    }
  } catch (e) {}
  try {
    const fbp = readCookie("_fbp");
    const fbc = readCookie("_fbc");
    const token = (typeof localStorage !== "undefined" && localStorage.getItem("id_token")) || null;
    const headers = { "Content-Type": "application/json" };
    if (token) headers["Authorization"] = "Bearer " + token;
    axios.post(
      `${API}/api/meta/track`,
      {
        event_name: eventName,
        event_id: eventId,
        source_url: typeof window !== "undefined" ? window.location.href : "",
        fbp: fbp || undefined,
        fbc: fbc || undefined,
        custom_data: customData,
      },
      { headers, adapter: "xhr", timeout: 5000 },
    ).catch(() => {});
  } catch (e) {}
  return eventId;
}

/** Pixel-only PageView — used for SPA route changes. The CAPI server-side
 *  pageview is omitted on purpose: PageView is high volume + low value
 *  for advanced matching, and server-side events are rate-limited. */
export function trackPageView() {
  try {
    if (typeof window !== "undefined" && typeof window.fbq === "function") {
      window.fbq("track", "PageView");
    }
  } catch (e) {}
}
